/**
 * ReportGenScreen — Raportti (Tulokset): create the workshop report, translate
 * it, set what the public report shows, and name its readers (#1475). The report step at the end of the
 * chain in Työpaja links here. Route: /reportgen?instance={instanceId}
 *
 * Separate from the public report (/raportti) — this is the admin control panel.
 */
import React, { useState, useEffect, useCallback } from 'react';
import styled from '@emotion/styled';
import { Link } from 'react-router-dom';
import { FileText, ExternalLink, Trash2, Clock, User, BarChart2 } from 'lucide-react';
import AdminHeader from '../components/AdminHeader';
import {
  ActionRow, SecondaryButton, SectionLabel, StatusMessage,
} from '../components/ReportAdmin/reportAdminStyles';
import { API_ENDPOINTS } from '../config/api';
import { useTranslation } from '../hooks/useTranslation.jsx';
import useAdminInstance from '../hooks/useAdminInstance';
import ReportReadersSection from '../components/ReportAdmin/ReportReadersSection';

const Container = styled.div`
  min-height: 100vh;
  background: linear-gradient(180deg, #0a0a0a 0%, #1a1a2e 100%);
`;

const Content = styled.div`
  padding: 48px;
  max-width: 900px;
  margin: 0 auto;
`;

const PageTitle = styled.h1`
  font-size: 24px;
  font-weight: 700;
  color: #fff;
  margin-bottom: 8px;
`;

const PageDescription = styled.p`
  font-size: 14px;
  color: #888;
  margin-bottom: 32px;
`;

const HistoryList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const HistoryCard = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 20px;
  background: rgba(255,255,255,0.04);
  border: 1px solid #222;
  border-radius: 10px;
  transition: border-color 0.2s;
  &:hover { border-color: #444; }
`;

const HistoryInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const HistoryTitle = styled.span`
  font-size: 14px;
  font-weight: 500;
  color: #fff;
`;

const HistoryMeta = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  font-size: 12px;
  color: #666;
`;

const MetaItem = styled.span`
  display: flex;
  align-items: center;
  gap: 4px;
`;

const DeleteButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 6px;
  background: transparent;
  border: 1px solid transparent;
  color: #666;
  cursor: pointer;
  transition: all 0.15s;
  &:hover { background: rgba(255,68,68,0.1); border-color: rgba(255,68,68,0.3); color: #ff4444; }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 48px 20px;
  color: #555;
  font-size: 14px;
`;

const DATE_LOCALES = { fi: 'fi-FI', en: 'en-GB', sv: 'sv-SE' };

function formatDate(dateStr, language) {
  if (!dateStr) return '—';
  const locale = DATE_LOCALES[language] || DATE_LOCALES.fi;
  return new Date(dateStr).toLocaleString(locale, {
    day: 'numeric', month: 'numeric', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
}

function reportTypeLabel(type, t) {
  switch (type) {
    case 'full': return t('reportGen.type.full');
    case 'summary': return t('reportGen.type.summary');
    case 'pivot-only': return t('reportGen.type.pivotOnly');
    default: return type || t('reportGen.type.fallback');
  }
}

export default function ReportGenScreen() {
  const { instanceId } = useAdminInstance();
  const { t, language, setLanguage } = useTranslation();

  const [instanceInfo, setInstanceInfo] = useState(null);
  const [reports, setReports] = useState([]);
  const [status, setStatus] = useState(null);

  // Fetch instance info + report history
  useEffect(() => {
    if (!instanceId) return;

    async function load() {
      try {
        const [instRes, reportsRes] = await Promise.all([
          fetch(API_ENDPOINTS.INSTANCE(instanceId)),
          fetch(API_ENDPOINTS.REPORTS(instanceId)),
        ]);
        if (instRes.ok) setInstanceInfo(await instRes.json());
        if (reportsRes.ok) setReports(await reportsRes.json());
      } catch (err) {
        console.error('Failed to load report data:', err);
      }
    }
    load();
  }, [instanceId]);

  useEffect(() => {
    const raw = instanceInfo?.language ?? instanceInfo?.settings?.language;
    if (raw && String(raw).trim() !== '') setLanguage(String(raw).trim());
  }, [instanceInfo?.language, instanceInfo?.settings?.language, setLanguage]);

  const handleDelete = useCallback(async (reportId) => {
    try {
      const res = await fetch(API_ENDPOINTS.REPORT_DELETE(reportId), { method: 'DELETE' });
      if (!res.ok) throw new Error('Delete failed');
      setReports(prev => prev.filter(r => r.id !== reportId));
    } catch (err) {
      console.error('Error deleting report:', err);
      setStatus({ text: t('reportGen.status.deleteFailed'), error: true });
    }
  }, [t]);

  const publicUrl = instanceId ? `/raportti?instance=${instanceId}` : null;

  return (
    <Container>
      <div style={{ background: '#1a1a2e' }}>
        <AdminHeader instanceId={instanceId} instanceName={instanceInfo?.title || t('reportGen.instanceFallback')} />
      </div>

      <Content>
        <PageTitle>{t('reportGen.title')}</PageTitle>
        <PageDescription>{t('reportGen.description')}</PageDescription>

        {!instanceId && (
          <EmptyState>{t('reportGen.selectInstance')}</EmptyState>
        )}

        {instanceId && (
          <>
            {status && (
              <StatusMessage $error={status.error}>{status.text}</StatusMessage>
            )}

            {/* Creating, images, summaries, translations, settings and exclusions are the report step's console in Työpaja (#1230) */}
            <PageDescription>
              {t('reportGen.movedToWorkshop')}{' '}
              <Link to={`/admin/workshop?instance=${instanceId}`}>{t('reportGen.openWorkshop')}</Link>
            </PageDescription>

            <ActionRow>
              {publicUrl && reports.length > 0 && (
                <SecondaryButton
                  as="a"
                  href={publicUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ textDecoration: 'none' }}
                >
                  <ExternalLink size={16} />
                  {t('reportGen.openPublic')}
                </SecondaryButton>
              )}
            </ActionRow>

            <ReportReadersSection instanceId={instanceId} />

            <SectionLabel>{t('reportGen.history')}</SectionLabel>

            <HistoryList>
              {reports.length === 0 && (
                <EmptyState>{t('reportGen.empty')}</EmptyState>
              )}

              {reports.map(report => (
                <HistoryCard key={report.id}>
                  <HistoryInfo>
                    <HistoryTitle>
                      <FileText size={14} style={{ marginRight: 6, verticalAlign: -2, opacity: 0.5 }} />
                      {reportTypeLabel(report.report_type, t)}
                    </HistoryTitle>
                    <HistoryMeta>
                      <MetaItem>
                        <Clock size={12} />
                        {formatDate(report.generated_at, language)}
                      </MetaItem>
                      {report.generated_by && (
                        <MetaItem>
                          <User size={12} />
                          {report.generated_by}
                        </MetaItem>
                      )}
                      {(report.participant_count != null || report.node_count != null) && (
                        <MetaItem>
                          <BarChart2 size={12} />
                          {report.participant_count != null && t('reportGen.meta.participants', { count: report.participant_count })}
                          {report.node_count != null && t('reportGen.meta.nodes', { count: report.node_count })}
                        </MetaItem>
                      )}
                    </HistoryMeta>
                  </HistoryInfo>

                  <DeleteButton onClick={() => handleDelete(report.id)} title={t('common.delete')}>
                    <Trash2 size={14} />
                  </DeleteButton>
                </HistoryCard>
              ))}
            </HistoryList>
          </>
        )}
      </Content>
    </Container>
  );
}
